import { createSelector } from "@reduxjs/toolkit";
import { AppRootState } from "./appStore";
import { XFrontend } from "../lib/XFrontend";
import { Capacitor } from "@capacitor/core";
import { getApiBaseUrl, getBuildNumber, getJsBundle } from "../lib/appInfo";
import Axios, { AxiosHeaders, AxiosInstance } from "axios";
import { TRAINING_MODE_UPN } from "../lib/trainingMode";
import { SettingId } from "@fusion-advantage/backcompat"

const simulateOfflineResponseInterceptor = () => {
	throw new Error('Network Error')
}

export const selectNewPlainAxiosCreator = createSelector(
	// cache forever
	(state: AppRootState) => true,
	() => {
		return () => Axios.create()
	}
)

// Use this instance type for non-Fusion API calls
// including direct calls against Azure Blob Storage
export const selectPlainAxios = createSelector(
	selectNewPlainAxiosCreator,
	(state: AppRootState) => state.app.simulateOffline,
	(createInstance, simulateOffline) => {
		const instance = createInstance()
		if(simulateOffline) {
			instance.interceptors.response.use(
				simulateOfflineResponseInterceptor
			)
		}
		return instance
	}
)

const selectFusionApiHeaders = createSelector(
	(state: AppRootState) => state.app[SettingId.BearerToken]?.token,
	(state: AppRootState) => state.app.isTrainingMode,
	(token, isTrainingMode): Partial<AxiosHeaders> => {
		const frontend: XFrontend = {
			Platform: Capacitor.getPlatform(),
			JsBundle: getJsBundle(),
			MobileBuild: getBuildNumber(),
		}
		// console.debug(`selectFusionApiHeaders token ${token}`)
		const headers: Partial<AxiosHeaders> = {
			'Authorization': token ? `Bearer ${token}` : undefined,
			'X-Frontend': JSON.stringify(frontend),
			'X-Frontend-Impersonate': isTrainingMode ? TRAINING_MODE_UPN : undefined,
		}
		return headers
	}
)

// TODO reconcile this
// created for experimental use with CloudDeviceSync
export const selectNewFusionApiAxiosCreator = createSelector(
	selectFusionApiHeaders,
	(headers) => {
		return (): AxiosInstance => {
			return Axios.create({
				baseURL: getApiBaseUrl(),
				withCredentials: true,
				headers,
			})
		}
	}
)

// Use this instance type to access the Fusion API
export const selectFusionApiAxios = createSelector(
	selectNewFusionApiAxiosCreator,
	(state: AppRootState) => state.app.simulateOffline,
	(createInstance, simulateOffline) => {
		const instance = createInstance()
		if(simulateOffline) {
			instance.interceptors.response.use(
				simulateOfflineResponseInterceptor
			)
		}
		return instance
	}
)
